import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config';
import logger from '../../utils/logger';
import { api, getApiBaseUrl } from '../../services/api';
import { getImageUrl } from '../../utils/imageUrl';
import { loadArticleHtml, prepareArticleQuill } from '../../utils/articlePaste';
import './VocabularyManagement.css';

// Main API for articles and data
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Image upload API
// Empty = use main server /api/upload-image
const IMAGE_UPLOAD_URL = process.env.REACT_APP_IMAGE_UPLOAD_URL || '';

const VocabularyManagement = ({ initialInnerTab = 'articles', selectedLanguage = 'English' }) => {
  const { t } = useTranslation('vocabularyManagement');
  const [activeInnerTab, setActiveInnerTab] = useState(initialInnerTab); // articles | jobs
  const [activeSchoolTab, setActiveSchoolTab] = useState('high-school');
  const [activeYearTab, setActiveYearTab] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editArticleId, setEditArticleId] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [yearArticleCounts, setYearArticleCounts] = useState({});
  const [formData, setFormData] = useState({
    title: '',
    contentType: '',
    schoolLevel: '',
    year: '',
    content: '',
    unit: '',
    unitId: null,
    imageFile: null,
    imagePreview: null
  });
  const [yearUnitsFromApi, setYearUnitsFromApi] = useState([]);
  const quillRef = useRef(null);
  const [useQuill, setUseQuill] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedArticleForDetails, setSelectedArticleForDetails] = useState(null);

  // Background processing job state
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState('');
  const [jobsStatusFilter, setJobsStatusFilter] = useState(''); // pending|running|succeeded|failed|''
  const [jobByArticleId, setJobByArticleId] = useState({}); // { [articleId]: jobRow }
  const [jobsArticleFilter, setJobsArticleFilter] = useState(''); // article_id or ''

  // ✅ ENGLISH KEYS (for API & database) - NEVER TRANSLATE THESE
  const SCHOOL_KEYS = {
    'high-school': 'High School',
    'middle-school': 'Middle School',
    'primary-school': 'Primary School'
  };

  const YEAR_KEYS = {
    'high-1': 'First Year',
    'high-2': 'Second Year',
    'high-3': 'Third Year',
    'middle-1': 'First Year',
    'middle-2': 'Second Year',
    'middle-3': 'Third Year',
    'middle-4': 'Fourth Year',
    'primary-1': 'First Year',
    'primary-2': 'Second Year',
    'primary-3': 'Third Year'
  };

  // Third Year High School Units per language (keys; labels via i18n)
  const THIRD_YEAR_UNIT_KEYS = {
    english: [
      'ethicsInBusiness',
      'ancientCivilizations',
      'safetyFirst',
      'education',
      'astronomy',
      'feelingsAndEmotions'
    ],
    // For now we reuse the same structure for other languages.
    // You can customize these arrays per language to match the exact curriculum.
    spanish: [
      'ethicsInBusiness',
      'ancientCivilizations',
      'safetyFirst',
      'education',
      'astronomy',
      'feelingsAndEmotions'
    ],
    italian: [
      'ethicsInBusiness',
      'ancientCivilizations',
      'safetyFirst',
      'education',
      'astronomy',
      'feelingsAndEmotions'
    ]
  };

  const getI18nLngForSelectedLanguage = (langName) => {
    const l = String(langName || '').trim().toLowerCase();
    if (l === 'english') return 'en';
    if (l === 'french') return 'fr';
    if (l === 'arabic') return 'ar';
    return null;
  };

  const normalizedLang = (selectedLanguage || 'English').toLowerCase();
  let thirdYearUnits;

  if (normalizedLang === 'german') {
    // German: use the exact units provided for the Algerian curriculum
    thirdYearUnits = [
      'Die Umweltprobleme',
      'Die Massenmedien und Kommunikationsmittel',
      'Jugendprobleme',
      'Die deutschsprachigen Länder',
      'Künstlerleben',
      'Der technische Fortschritt'
    ];
  } else if (normalizedLang === 'french') {
    // French (Algerian curriculum): exact unit titles by unit number (1..3),
    // independent of the admin UI language.
    thirdYearUnits = [
      "Le texte d'histoire",
      'Le débat des idées',
      "L'appel"
    ];
  } else if (normalizedLang === 'italian') {
    // Italian (Algerian curriculum, 3rd year high school): themes with proper Italian spelling.
    thirdYearUnits = [
      'La famiglia',
      "L'ambiente",
      'La cucina',
      'La moda',
      'La tecnologia',
      'La salute',
      'Lo sport'
    ];
  } else if (normalizedLang === 'spanish') {
    // Spanish (Algerian curriculum, 3rd year high school): themes with proper Spanish spelling.
    thirdYearUnits = [
      'El medio ambiente',
      'La educación',
      'La ciencia y la tecnología',
      'La solidaridad',
      'La salud'
    ];
  } else {
    const unitKeysForLang =
      THIRD_YEAR_UNIT_KEYS[normalizedLang] || THIRD_YEAR_UNIT_KEYS.english;
    // Use fixed translator so the unit labels follow the selected content language,
    // not the current admin UI language (i18n language).
    const fixedLng = getI18nLngForSelectedLanguage(selectedLanguage) || 'en';
    const fixedT = i18n.getFixedT(fixedLng, 'vocabularyManagement');
    thirdYearUnits = unitKeysForLang.map((key) => fixedT(`units.${key}`));
  }

  // French 4AM (4th year middle school) – Algerian curriculum sequences
  const FRENCH_FOURTH_YEAR_MIDDLE_UNITS = [
    'Bienvenue dans ma région !',
    'Gloire à nos ancêtres !',
    'Oui à la culture !',
    'Vivons en harmonie !',
    'Non à la violence !',
    'Protégeons la nature !',
    'Agissons en écoresponsables !'
  ];

  const useDbUnits = yearUnitsFromApi.length > 0;
  const showLegacyUnitDropdown =
    !useDbUnits &&
    ((activeSchoolTab === 'high-school' && activeYearTab === 'high-3') ||
      (activeSchoolTab === 'middle-school' && activeYearTab === 'middle-4' && normalizedLang === 'french'));
  const showUnitDropdown = useDbUnits || showLegacyUnitDropdown;
  const formUnits =
    activeSchoolTab === 'middle-school' && activeYearTab === 'middle-4' && normalizedLang === 'french'
      ? FRENCH_FOURTH_YEAR_MIDDLE_UNITS
      : thirdYearUnits;

  // School tabs - UI labels (translated)
  const schoolTabs = [
    { id: 'high-school', label: t('schoolLevels.highSchool'), icon: 'school' },
    { id: 'middle-school', label: t('schoolLevels.middleSchool'), icon: 'school' },
    { id: 'primary-school', label: t('schoolLevels.primarySchool'), icon: 'school' }
  ];

  // School levels - UI labels (translated) + English keys (for API)
  // NOTE: This is the base structure, language-agnostic.
  const baseSchoolLevels = {
    'high-school': {
      name: t('schoolLevels.highSchool'), // UI label (translated)
      key: SCHOOL_KEYS['high-school'], // English key for API
      years: [
        { id: 'high-1', name: t('yearLevels.firstYear'), key: YEAR_KEYS['high-1'] },
        { id: 'high-2', name: t('yearLevels.secondYear'), key: YEAR_KEYS['high-2'] },
        { id: 'high-3', name: t('yearLevels.thirdYear'), key: YEAR_KEYS['high-3'] }
      ]
    },
    'middle-school': {
      name: t('schoolLevels.middleSchool'), // UI label (translated)
      key: SCHOOL_KEYS['middle-school'], // English key for API
      years: [
        { id: 'middle-1', name: t('yearLevels.firstYear'), key: YEAR_KEYS['middle-1'] },
        { id: 'middle-2', name: t('yearLevels.secondYear'), key: YEAR_KEYS['middle-2'] },
        { id: 'middle-3', name: t('yearLevels.thirdYear'), key: YEAR_KEYS['middle-3'] },
        { id: 'middle-4', name: t('yearLevels.fourthYear'), key: YEAR_KEYS['middle-4'] }
      ]
    },
    'primary-school': {
      name: t('schoolLevels.primarySchool'), // UI label (translated)
      key: SCHOOL_KEYS['primary-school'], // English key for API
      years: [
        { id: 'primary-1', name: t('yearLevels.firstYear'), key: YEAR_KEYS['primary-1'] },
        { id: 'primary-2', name: t('yearLevels.secondYear'), key: YEAR_KEYS['primary-2'] },
        { id: 'primary-3', name: t('yearLevels.thirdYear'), key: YEAR_KEYS['primary-3'] }
      ]
    }
  };

  // Language-specific adjustments (Algerian educational system)
  // German/Italian/Spanish are only studied in 2nd & 3rd year of High School.
  const lang = (selectedLanguage || '').toLowerCase();
  const isHighSchoolOnlyLanguage = ['german', 'italian', 'spanish'].includes(lang);

  const schoolLevels = isHighSchoolOnlyLanguage
    ? {
        ...baseSchoolLevels,
        'high-school': {
          ...baseSchoolLevels['high-school'],
          years: baseSchoolLevels['high-school'].years.filter((y) => y.id !== 'high-1')
        }
      }
    : baseSchoolLevels;

  // If admin switches to a "high-school-only" language while currently viewing
  // Primary/Middle, force the UI back to High School to avoid showing irrelevant tabs.
  useEffect(() => {
    if (isHighSchoolOnlyLanguage && activeSchoolTab !== 'high-school') {
      setActiveSchoolTab('high-school');
      setActiveYearTab(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  // Quill setup
  useEffect(() => {
    if (showForm && window.Quill && quillRef.current && !useQuill) {
      setUseQuill(true);
      quillRef.current.innerHTML = '';
      const quill = new window.Quill(quillRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'align': [] }], // Alignment dropdown with all options
            ['link', 'blockquote', 'code-block'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'header': [1, 2, 3, false] }],
            [{ 'color': [] }, { 'background': [] }], // Text and background colors
            ['clean']
          ]
        },
        placeholder: t('form.contentPlaceholder')
      });
      quill.on('text-change', () => {
        setFormData(f => ({ ...f, content: quill.root.innerHTML }));
      });
      prepareArticleQuill(quill);
    }
    // eslint-disable-next-line
  }, [showForm]);

  useEffect(() => {
    setActiveYearTab(null);
    setArticles([]);
    setError('');
    fetchYearArticleCounts();
  }, [activeSchoolTab]);

  useEffect(() => {
    setActiveInnerTab(initialInnerTab);
    // eslint-disable-next-line
  }, [initialInnerTab]);

  useEffect(() => {
    if (activeYearTab) {
      fetchArticles();
    }
    // eslint-disable-next-line
  }, [activeYearTab]);

  useEffect(() => {
    const schoolKey = SCHOOL_KEYS[activeSchoolTab];
    const yearKey = YEAR_KEYS[activeYearTab];
    if (!schoolKey || !yearKey) {
      setYearUnitsFromApi([]);
      return;
    }
    let cancelled = false;
    const base = getApiBaseUrl();
    const q = new URLSearchParams({ school: schoolKey, year: yearKey });
    if (selectedLanguage) q.set('language', selectedLanguage);
    fetch(`${base}/api/explore/units?${q}`)
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) setYearUnitsFromApi(Array.isArray(d?.units) ? d.units : []);
      })
      .catch(() => {
        if (!cancelled) setYearUnitsFromApi([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeSchoolTab, activeYearTab, selectedLanguage]);

  useEffect(() => {
    // Keep job badges in sync with currently selected school/year.
    // This is best-effort; if admin token is missing, it will fail silently.
    if (activeSchoolTab && activeYearTab) {
      fetchJobsForBadges();
    } else {
      setJobByArticleId({});
    }
    // eslint-disable-next-line
  }, [activeSchoolTab, activeYearTab]);

  useEffect(() => {
    if (activeInnerTab === 'jobs') {
      fetchJobs();
    }
    // eslint-disable-next-line
  }, [activeInnerTab, activeSchoolTab, activeYearTab, jobsStatusFilter]);

  async function fetchJobs() {
    setJobsLoading(true);
    setJobsError('');
    try {
      const level = schoolLevels[activeSchoolTab];
      const yearObj = level?.years.find(y => y.id === activeYearTab);

      const params = new URLSearchParams();
      if (jobsStatusFilter) params.set('status', jobsStatusFilter);
      if (jobsArticleFilter) params.set('article_id', jobsArticleFilter);

      // Filter jobs to the currently selected educational level + year (if selected)
      // Uses English keys because articles table stores English values.
      params.set('type', 'vocabulary');
      if (level?.key) params.set('school', level.key);
      if (yearObj?.key) params.set('year', yearObj.key);

      // Admin endpoint requires auth; api service attaches Bearer token automatically.
      const result = await api.get(`/api/admin/article-processing-jobs?${params.toString()}`);
      setJobs(result.jobs || []);
    } catch (e) {
      setJobsError(e.message || 'Failed to load jobs');
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  }

  async function fetchJobsForBadges() {
    try {
      const level = schoolLevels[activeSchoolTab];
      const yearObj = level?.years.find(y => y.id === activeYearTab);
      if (!level?.key || !yearObj?.key) return;

      const params = new URLSearchParams();
      params.set('type', 'vocabulary');
      params.set('school', level.key);
      params.set('year', yearObj.key);

      const result = await api.get(`/api/admin/article-processing-jobs?${params.toString()}`);
      const rows = result.jobs || [];
      const map = {};
      rows.forEach((j) => {
        if (j?.article_id != null) map[j.article_id] = j;
      });
      setJobByArticleId(map);
    } catch (e) {
      // No badge data if auth missing or endpoint fails.
      setJobByArticleId({});
    }
  }

  async function reprocessArticle(articleId) {
    try {
      await api.post(`/api/admin/articles/${articleId}/reprocess-words`, {});
      await fetchJobs();
      await fetchJobsForBadges();
      alert('Reprocess queued successfully');
    } catch (e) {
      alert(`Failed to reprocess: ${e.message || e}`);
    }
  }

  function openJobsForArticle(articleId, status) {
    setActiveInnerTab('jobs');
    setJobsArticleFilter(String(articleId));
    if (status) setJobsStatusFilter(status);
  }

  // Fetch article counts for all years of the current school level
  async function fetchYearArticleCounts() {
    const level = schoolLevels[activeSchoolTab];
    if (!level) return;

    try {
      const params = new URLSearchParams();
      if (selectedLanguage) params.set('language', selectedLanguage);
      const data = await api.get(`/dashboard/articles?${params.toString()}`);
      const list = Array.isArray(data) ? data : [];
      const counts = {};
      const langNorm = selectedLanguage ? String(selectedLanguage).trim().toLowerCase() : '';
      for (const year of level.years) {
        const n = list.filter(article =>
          article.school === level.key &&
          article.year === year.key &&
          article.type === 'vocabulary' &&
          (!langNorm || (article.language && String(article.language).trim().toLowerCase() === langNorm))
        ).length;
        counts[year.id] = n;
      }
      setYearArticleCounts(counts);
    } catch (err) {
      logger.error('Error fetching year article counts:', err);
      const counts = {};
      level.years.forEach(year => { counts[year.id] = 0; });
      setYearArticleCounts(counts);
    }
  }

  // Fetch articles for the selected school/year (admin: use dashboard endpoint so auth works)
  async function fetchArticles() {
    setLoading(true);
    setError('');
    const level = schoolLevels[activeSchoolTab];
    const yearObj = level?.years.find(y => y.id === activeYearTab);
    if (!level || !yearObj) return;

    const schoolKey = level.key;
    const yearKey = yearObj.key;

    logger.log('🔍 Fetching articles for:', { school: schoolKey, year: yearKey, type: 'vocabulary' });

    try {
      // Admin panel: GET /dashboard/articles (returns all articles; no user subscription required)
      const params = new URLSearchParams();
      if (selectedLanguage) params.set('language', selectedLanguage);
      const data = await api.get(`/dashboard/articles?${params.toString()}`);
      const list = Array.isArray(data) ? data : [];
      logger.log('📚 Received articles:', list.length);

      const langNorm = selectedLanguage ? String(selectedLanguage).trim().toLowerCase() : '';
      const filteredArticles = list.filter(article =>
        article.school === schoolKey &&
        article.year === yearKey &&
        article.type === 'vocabulary' &&
        (!langNorm || (article.language && String(article.language).trim().toLowerCase() === langNorm))
      );

      logger.log('✅ Filtered articles for exact match:', filteredArticles.length);

      setArticles(filteredArticles);
      setError('');
      setYearArticleCounts(prev => ({
        ...prev,
        [activeYearTab]: filteredArticles.length
      }));
    } catch (err) {
      logger.error('❌ Error fetching articles:', err);
      setError('Unable to load articles. Please try again later.');
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }

  // Real image upload function - Connects to image upload server
  async function uploadImageToServer(file) {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${IMAGE_UPLOAD_URL}/api/upload-image`, {
        method: 'POST',
        body: formData
      });

      const text = await response.text();
      if (!response.ok) {
        let msg = 'Failed to upload image';
        try {
          const data = JSON.parse(text);
          if (data.error) msg = data.error;
        } catch (_) {
          if (response.status === 413) msg = 'Image is too large. Maximum size is 20MB. Try a smaller image or compress it.';
        }
        throw new Error(msg);
      }

      const result = JSON.parse(text);
      return result;
    } catch (error) {
      logger.error('Image upload error:', error);
      throw error;
    }
  }

  // Add or update article
  async function handleFormSubmit(e) {
    e.preventDefault();
    const level = schoolLevels[activeSchoolTab];
    const yearObj = level?.years.find(y => y.id === activeYearTab);
    if (!formData.title || !formData.contentType || !yearObj || !formData.content) {
      alert(t('alerts.fillAllFields'));
      return;
    }
    
    if (useDbUnits && (formData.unitId == null || formData.unitId === '')) {
      alert(t('alerts.selectUnitThirdYear'));
      return;
    }
    if (showLegacyUnitDropdown && !formData.unit) {
      alert(t('alerts.selectUnitThirdYear'));
      return;
    }
    
    // Handle image upload first if there's a new image
    let imageUrl = null;
    if (formData.imageFile) {
      try {
        logger.log('🖼️ Starting image upload...', formData.imageFile.name);
        // Upload image to backend server
        const uploadResult = await uploadImageToServer(formData.imageFile);
        logger.log('📤 Image upload result:', uploadResult);
        if (uploadResult.success) {
          imageUrl = uploadResult.imageUrl;
          logger.log('✅ Image uploaded successfully:', imageUrl);
        } else {
          throw new Error('Failed to upload image');
        }
      } catch (error) {
        logger.error('❌ Image upload error:', error);
        alert(t('alerts.errorUploadingImage', { message: error.message }));
        return;
      }
    } else {
      logger.log('ℹ️ No image file selected');
    }
    
    logger.log('📝 Creating article with image URL:', imageUrl);
    
    // ✅ Use English keys for API payload (database expects English)
    const articleData = {
      title: formData.title,
      content: formData.content,
      type: 'vocabulary', // Always set to vocabulary for this component
      school: level.key, // ✅ English key: "High School" (NOT translated label)
      year: yearObj.key, // ✅ English key: "First Year" (NOT translated label)
      language: selectedLanguage || 'English',
      country: 'Algeria',
      teacher_id: 'admin',
      unit: formData.unit || null,
      unit_id:
        formData.unitId != null && formData.unitId !== ''
          ? Number(formData.unitId)
          : null,
      image_url: imageUrl
    };
    
    logger.log('📊 Article data being sent:', articleData);
    try {
      let res;
      if (isEditing && editArticleId) {
        res = await fetch(`${API_BASE_URL}/articles/${editArticleId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(articleData)
        });
      } else {
        res = await fetch(`${API_BASE_URL}/articles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(articleData)
        });
      }
      if (!res.ok) throw new Error('Failed to save article');
      setShowForm(false);
      setIsEditing(false);
      setEditArticleId(null);
      setFormData({ title: '', contentType: 'vocabulary', schoolLevel: '', year: '', content: '', unit: '', unitId: null, imageFile: null, imagePreview: null });
      fetchArticles();
      fetchYearArticleCounts(); // Refresh all year counts
      alert(isEditing ? t('alerts.articleUpdated') : t('alerts.articleAdded'));
    } catch (err) {
      alert(t('alerts.errorSaving'));
    }
  }

  // Delete article
  async function handleDelete(article) {
    if (!window.confirm(t('alerts.confirmDelete', { title: article.title }))) return;
    try {
      const res = await fetch(`${API_BASE_URL}/articles/${article.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete');
      fetchArticles();
      fetchYearArticleCounts(); // Refresh all year counts
      alert(t('alerts.articleDeleted'));
    } catch (err) {
      alert(t('alerts.errorDeleting'));
    }
  }

  // Edit article
  function handleEdit(article) {
    setShowForm(true);
    setIsEditing(true);
    setEditArticleId(article.id);
    setFormData({
      title: article.title,
      contentType: article.type,
      schoolLevel: article.school,
      year: article.year,
      content: article.content,
      unit: article.unit,
      unitId: article.unit_id != null ? Number(article.unit_id) : null,
      imageFile: null,
      imagePreview: article.image_url ? (getImageUrl(article.image_url) || article.image_url) : null
    });
    setTimeout(() => {
      if (window.Quill && quillRef.current) {
        const quill = window.Quill.find(quillRef.current);
        if (quill) loadArticleHtml(quill, article.content);
      }
    }, 200);
  }

  // Form modal open/close
  function openForm() {
    setShowForm(true);
    setIsEditing(false);
    setEditArticleId(null);
    setFormData({ title: '', contentType: '', schoolLevel: '', year: '', content: '', unit: '', unitId: null, imageFile: null, imagePreview: null });
    setTimeout(() => {
      if (window.Quill && quillRef.current) {
        const quill = window.Quill.find(quillRef.current);
        if (quill) quill.root.innerHTML = '';
      }
    }, 200);
  }

  const renderBreadcrumb = () => {
    const school = schoolLevels[activeSchoolTab]?.name;
    const year = schoolLevels[activeSchoolTab]?.years.find(y => y.id === activeYearTab)?.name;
    return (
      <div className="breadcrumb">
        <span className="breadcrumb-item">{school}</span>
        {year && <span className="breadcrumb-sep">›</span>}
        {year && <span className="breadcrumb-item">{year}</span>}
      </div>
    );
  };

  const renderSchoolTabs = () => {
    const tabsToShow = isHighSchoolOnlyLanguage
      ? schoolTabs.filter((t) => t.id === 'high-school')
      : schoolTabs;

    return (
      <div className="vocabulary-tabs">
        {tabsToShow.map((tab) => (
          <button
            key={tab.id}
            className={`vocabulary-tab ${activeSchoolTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveSchoolTab(tab.id)}
          >
            <span className="material-icons">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>
    );
  };

  const renderYearTabs = () => {
    const level = schoolLevels[activeSchoolTab];
    if (!level) return null;
    return (
      <div className="year-tabs">
        <div className="year-tabs-header">
          <h3>{level.name} {t('yearLevels.years')}</h3>
          <p>{t('messages.selectYearLevel')}</p>
        </div>
        <div className="year-tabs-container">
          {level.years.map((year) => (
            <button
              key={year.id}
              className={`year-tab ${activeYearTab === year.id ? 'active' : ''}`}
              onClick={() => setActiveYearTab(year.id)}
            >
              <span className="material-icons">grade</span>
              {year.name}
              {activeYearTab === year.id && <span className="year-tab-indicator" />}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    const level = schoolLevels[activeSchoolTab];
    const selectedYear = level?.years.find(y => y.id === activeYearTab);
    
    // Add safety checks for undefined values
    if (!level) {
      return (
        <div className="vocabulary-tab-content fade-in">
          <div className="vocabulary-header">
            <h2>{t('title')}</h2>
            <p>{t('messages.selectSchoolLevel')}</p>
          </div>
        </div>
      );
    }
    
    if (!activeYearTab) {
      return (
        <div className="vocabulary-tab-content fade-in">
          <div className="vocabulary-header">
            <h2>{level.name} {t('title')}</h2>
            <p>{t('messages.selectYearLevelFromTabs', { school: level.name })}</p>
          </div>
          {/* Inner tabs (Articles / Translation Jobs) */}
          <div className="vocabulary-inner-tabs">
            <button
              className={`vocabulary-inner-tab ${activeInnerTab === 'articles' ? 'active' : ''}`}
              onClick={() => setActiveInnerTab('articles')}
            >
              <span className="material-icons">library_books</span>
              Articles
            </button>
            <button
              className={`vocabulary-inner-tab ${activeInnerTab === 'jobs' ? 'active' : ''}`}
              onClick={() => setActiveInnerTab('jobs')}
            >
              <span className="material-icons">sync</span>
              Translation Jobs
            </button>
          </div>
          {activeInnerTab === 'articles' ? (
            <div className="year-overview">
              <h3>{t('messages.yearLevelsOverview')}</h3>
              <div className="year-cards">
                {level.years.map((year) => (
                  <div key={year.id} className="year-card">
                    <div className="year-card-header">
                      <span className="material-icons">grade</span>
                      <h4>{year.name}</h4>
                    </div>
                    <div className="year-card-content">
                      <p>{t('messages.readingArticles', { count: yearArticleCounts[year.id] || 0 })}</p>
                    </div>
                    <button 
                      className="select-year-btn"
                      onClick={() => setActiveYearTab(year.id)}
                    >
                      {t('actions.manageContent')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="content-section">
              <div className="jobs-header">
                <h3>Translation Jobs</h3>
                <div className="jobs-controls">
                  <select
                    value={jobsStatusFilter}
                    onChange={(e) => setJobsStatusFilter(e.target.value)}
                  >
                    <option value="">All statuses</option>
                    <option value="pending">pending</option>
                    <option value="running">running</option>
                    <option value="succeeded">succeeded</option>
                    <option value="failed">failed</option>
                  </select>
                  <button type="button" className="btn-secondary" onClick={fetchJobs}>
                    Refresh
                  </button>
                </div>
              </div>

              {jobsLoading ? (
                <div className="loading">Loading jobs...</div>
              ) : jobsError ? (
                <div className="empty-state">
                  <span className="material-icons empty-icon">error_outline</span>
                  <p>{jobsError}</p>
                </div>
              ) : jobs.length === 0 ? (
                <div className="empty-state">
                  <span className="material-icons empty-icon">inbox</span>
                  <p>No jobs found for this school level.</p>
                </div>
              ) : (
                <div className="jobs-table-wrapper">
                  <table className="jobs-table">
                    <thead>
                      <tr>
                        <th>Article</th>
                        <th>Status</th>
                        <th>Attempts</th>
                        <th>Last error</th>
                        <th>Started</th>
                        <th>Finished</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map((j) => (
                        <tr key={j.article_id}>
                          <td>
                            <div className="job-article-cell">
                              <div className="job-article-title">{j.article_title || `#${j.article_id}`}</div>
                              <div className="job-article-meta">#{j.article_id}</div>
                            </div>
                          </td>
                          <td><span className={`job-status ${j.status}`}>{j.status}</span></td>
                          <td>{j.attempts}</td>
                          <td className="job-error">{j.last_error || ''}</td>
                          <td>{j.started_at ? new Date(j.started_at).toLocaleString() : ''}</td>
                          <td>{j.finished_at ? new Date(j.finished_at).toLocaleString() : ''}</td>
                          <td>
                            <button
                              type="button"
                              className="btn-primary"
                              onClick={() => reprocessArticle(j.article_id)}
                            >
                              Reprocess
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    
    // Add safety check for selectedYear
    if (!selectedYear) {
      return (
        <div className="vocabulary-tab-content fade-in">
          <div className="vocabulary-header">
            <h2>{level.name} {t('title')}</h2>
            <p>{t('messages.invalidYearSelection')}</p>
          </div>
        </div>
      );
    }
    
    return (
      <div className="vocabulary-tab-content fade-in">
        <div className="vocabulary-header">
          <h2>{selectedYear.name} - {level.name}</h2>
          <p>{t('messages.manageContentFor', { year: selectedYear.name })}</p>
        </div>
        {/* Inner tabs (Articles / Translation Jobs) */}
        <div className="vocabulary-inner-tabs">
          <button
            className={`vocabulary-inner-tab ${activeInnerTab === 'articles' ? 'active' : ''}`}
            onClick={() => setActiveInnerTab('articles')}
          >
            <span className="material-icons">library_books</span>
            Articles
          </button>
          <button
            className={`vocabulary-inner-tab ${activeInnerTab === 'jobs' ? 'active' : ''}`}
            onClick={() => setActiveInnerTab('jobs')}
          >
            <span className="material-icons">sync</span>
            Translation Jobs
          </button>
        </div>
        {renderBreadcrumb()}
        <div className="divider" />
        {activeInnerTab === 'articles' ? (
          <>
            <div className="vocabulary-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('actions.searchPlaceholder')}
                  onChange={e => {
                    // Optionally implement search filtering here
                  }}
                />
                <span className="material-icons">search</span>
              </div>
              <button className="add-vocabulary-btn" onClick={openForm}>
                <span className="material-icons">add</span>
                {t('actions.addNewContent')}
              </button>
            </div>
            <div className="content-section">
              <h3>{t('contentFor', { year: selectedYear.name })}</h3>
              {loading ? (
                <div className="loading">{t('messages.loadingArticles')}</div>
              ) : error ? (
                <div className="empty-state">
                  <span className="material-icons empty-icon">error_outline</span>
                  <p>{error}</p>
                </div>
              ) : articles.length === 0 ? (
                <div className="empty-state">
                  <span className="material-icons empty-icon">library_books</span>
                  <p>{t('messages.noContentFound')}</p>
                </div>
              ) : (
                <div className="content-cards">
                  {articles.map((article) => (
                    <div key={article.id} className="content-card card-animate">
                      {/* Article Image or Placeholder */}
                      <div className="content-card-image">
                        {article.image_url ? (
                          <img 
                            src={getImageUrl(article.image_url) || article.image_url} 
                            alt={article.title}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        {/* Image Placeholder */}
                        <div className="image-placeholder" style={{ display: article.image_url ? 'none' : 'flex' }}>
                          <div className="placeholder-content">
                            <span className="material-icons placeholder-icon">image</span>
                            <span className="placeholder-text">{t('form.noImage')}</span>
                          </div>
                        </div>
                      </div>
                      <div className="content-title-row">
                        <h4 className="content-title">{article.title}</h4>
                        {jobByArticleId[article.id]?.status && (
                          <div className="article-job-badge-wrap">
                            <span
                              className={`article-job-badge ${jobByArticleId[article.id].status}`}
                              role="button"
                              tabIndex={0}
                              onClick={() => openJobsForArticle(article.id, jobByArticleId[article.id].status)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openJobsForArticle(article.id, jobByArticleId[article.id].status);
                                }
                              }}
                              title={
                                `Job: ${jobByArticleId[article.id].status}\n` +
                                `Attempts: ${jobByArticleId[article.id].attempts ?? 0}\n` +
                                (jobByArticleId[article.id].last_error ? `Last error: ${jobByArticleId[article.id].last_error}` : '')
                              }
                            >
                              {jobByArticleId[article.id].status}
                            </span>
                            {jobByArticleId[article.id].status === 'failed' && (
                              <button
                                type="button"
                                className="article-job-reprocess-btn"
                                title="Reprocess word translations"
                                onClick={() => reprocessArticle(article.id)}
                              >
                                <span className="material-icons">refresh</span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="preview-text">
                        {article.content ? 
                          (() => {
                            const cleanContent = article.content.replace(/<[^>]*>/g, '').trim();
                            if (cleanContent.length === 0) {
                              return 'This vocabulary lesson covers essential words and phrases for students...';
                            }
                            const preview = cleanContent.substring(0, 80); // Reduced to 80 characters for 2 lines
                            return preview.length < cleanContent.length ? `${preview}...` : preview;
                          })() : 
                          'This vocabulary lesson covers essential words and phrases for students...'
                        }
                      </div>
                      <div className="card-bottom-section">
                        <div className="content-actions">
                          <button className="action-btn edit" title={t('actions.editContent')} onClick={() => handleEdit(article)}>
                            <span className="material-icons">edit</span>
                            {t('actions.edit')}
                          </button>
                          <button className="action-btn delete" title={t('actions.delete')} onClick={() => handleDelete(article)}>
                            <span className="material-icons">delete</span>
                            {t('actions.delete')}
                          </button>
                        </div>
                        <div className="article-metadata">
                          <div className="metadata-item">
                            <span className="material-icons">school</span>
                            <span>{article.school}</span>
                          </div>
                          <div className="metadata-item">
                            <span className="material-icons">calendar_today</span>
                            <span>{article.year}</span>
                          </div>
                          <div className="metadata-item">
                            <span className="material-icons">schedule</span>
                            <span>{article.created_at ? new Date(article.created_at).toLocaleDateString() : ''}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="content-section">
            <div className="jobs-header">
              <h3>Translation Jobs</h3>
              <div className="jobs-controls">
                {jobsArticleFilter && (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setJobsArticleFilter('')}
                    title="Clear article filter"
                  >
                    Show all
                  </button>
                )}
                <select
                  value={jobsStatusFilter}
                  onChange={(e) => setJobsStatusFilter(e.target.value)}
                >
                  <option value="">All statuses</option>
                  <option value="pending">pending</option>
                  <option value="running">running</option>
                  <option value="succeeded">succeeded</option>
                  <option value="failed">failed</option>
                </select>
                <button type="button" className="btn-secondary" onClick={fetchJobs}>
                  Refresh
                </button>
              </div>
            </div>

            {jobsArticleFilter && (
              <div className="jobs-active-filter">
                Showing article #{jobsArticleFilter}
              </div>
            )}

            {jobsLoading ? (
              <div className="loading">Loading jobs...</div>
            ) : jobsError ? (
              <div className="empty-state">
                <span className="material-icons empty-icon">error_outline</span>
                <p>{jobsError}</p>
              </div>
            ) : jobs.length === 0 ? (
              <div className="empty-state">
                <span className="material-icons empty-icon">inbox</span>
                <p>No jobs found for this level/year.</p>
              </div>
            ) : (
              <div className="jobs-table-wrapper">
                <table className="jobs-table">
                  <thead>
                    <tr>
                      <th>Article</th>
                      <th>Status</th>
                      <th>Attempts</th>
                      <th>Last error</th>
                      <th>Started</th>
                      <th>Finished</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((j) => (
                      <tr key={j.article_id} className={jobsArticleFilter && String(j.article_id) === String(jobsArticleFilter) ? 'job-row-highlight' : ''}>
                        <td>
                          <div className="job-article-cell">
                            <div className="job-article-title">{j.article_title || `#${j.article_id}`}</div>
                            <div className="job-article-meta">#{j.article_id}</div>
                          </div>
                        </td>
                        <td><span className={`job-status ${j.status}`}>{j.status}</span></td>
                        <td>{j.attempts}</td>
                        <td className="job-error">{j.last_error || ''}</td>
                        <td>{j.started_at ? new Date(j.started_at).toLocaleString() : ''}</td>
                        <td>{j.finished_at ? new Date(j.finished_at).toLocaleString() : ''}</td>
                        <td>
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={() => reprocessArticle(j.article_id)}
                          >
                            Reprocess
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderForm = () => (
    <div className={`vocabulary-overlay ${showForm ? 'show' : ''}`}>
      <div className="vocabulary-form">
        <div className="form-header">
          <h3>
            {isEditing ? t('form.editContent') : t('form.addNewVocabularyContent')}
            {selectedLanguage && (
              <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 500, color: '#6c757d' }}>
                · {selectedLanguage}
              </span>
            )}
          </h3>
          <button className="close-btn" onClick={() => setShowForm(false)}>
            <span className="material-icons">close</span>
          </button>
        </div>
        <form onSubmit={handleFormSubmit} className="form-content">
          <div className="form-group">
            <label htmlFor="article-title">{t('form.title')}</label>
            <input 
              type="text" 
              id="article-title" 
              placeholder={t('form.titlePlaceholder')} 
              value={formData.title}
              onChange={e => setFormData(f => ({ ...f, title: e.target.value }))}
              required
            />
          </div>
          
          {/* Image Upload Field */}
          <div className="form-group">
            <label htmlFor="article-image">{t('form.articleImage')}</label>
            <div className="image-upload-container">
              <input 
                type="file" 
                id="article-image" 
                accept="image/*"
                onChange={e => {
                  const file = e.target.files[0];
                  if (file) {
                    setFormData(f => ({ 
                      ...f, 
                      imageFile: file,
                      imagePreview: URL.createObjectURL(file)
                    }));
                  }
                }}
                style={{ display: 'none' }}
              />
              <button 
                type="button" 
                className="image-upload-btn"
                onClick={() => document.getElementById('article-image').click()}
              >
                <span className="material-icons">add_photo_alternate</span>
                {formData.imagePreview ? t('form.changeImage') : t('form.chooseImage')}
              </button>
              
              {/* Image Preview */}
              {formData.imagePreview && (
                <div className="image-preview-container">
                  <img 
                    src={formData.imagePreview} 
                    alt="Preview" 
                    className="image-preview"
                  />
                  <button 
                    type="button" 
                    className="remove-image-btn"
                    onClick={() => setFormData(f => ({ 
                      ...f, 
                      imageFile: null, 
                      imagePreview: null 
                    }))}
                    title={t('form.removeImage')}
                  >
                    <span className="material-icons">close</span>
                  </button>
                </div>
              )}
            </div>
            <p className="form-help-text">
              {t('form.imageHelpText')}
            </p>
          </div>
          <div className="form-group">
            <label htmlFor="content-type">{t('form.type')}</label>
            <select 
              id="content-type" 
              value={formData.contentType}
              onChange={e => setFormData(f => ({ ...f, contentType: e.target.value }))}
              required
            >
              <option value="">{t('form.selectContentType')}</option>
              <option value="reading">{t('form.readingArticle')}</option>
              <option value="vocabulary">{t('form.vocabulary')}</option>
            </select>
          </div>
          
          {/* Unit: DB-backed when /api/explore/units returns rows; else legacy lists (HS3 / French 4AM) */}
          {showUnitDropdown && (
            <div className="form-group">
              <label htmlFor="article-unit">{t('form.unit')}</label>
              {useDbUnits ? (
                <select
                  id="article-unit"
                  value={formData.unitId != null && formData.unitId !== '' ? String(formData.unitId) : ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) {
                      setFormData((f) => ({ ...f, unitId: null, unit: '' }));
                      return;
                    }
                    const id = Number(v);
                    const u = yearUnitsFromApi.find((x) => Number(x.id) === id);
                    setFormData((f) => ({
                      ...f,
                      unitId: id,
                      unit: u?.title || ''
                    }));
                  }}
                  required
                >
                  <option value="">{t('form.selectUnit')}</option>
                  {yearUnitsFromApi.map((u) => (
                    <option key={u.id} value={String(u.id)}>
                      {u.unit_number != null ? `Unit ${u.unit_number} · ${u.title}` : u.title}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  id="article-unit"
                  value={formData.unit}
                  onChange={(e) => setFormData((f) => ({ ...f, unit: e.target.value, unitId: null }))}
                  required
                >
                  <option value="">{t('form.selectUnit')}</option>
                  {formUnits.map((unit, index) => (
                    <option key={index} value={unit}>
                      {unit}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="article-content">{t('form.content')}</label>
            {/* Quill or fallback textarea */}
            <div style={{ display: useQuill ? 'block' : 'none' }}>
              <div id="article-editor" ref={quillRef} style={{ minHeight: 120, background: '#fff', borderRadius: 8 }} />
            </div>
            <textarea
              id="fallback-editor"
              style={{ display: useQuill ? 'none' : 'block', width: '100%', minHeight: 120, padding: 12, border: '2px solid #e1e5e9', borderRadius: 8, fontSize: 16, fontFamily: 'Arial, sans-serif' }}
              placeholder={t('form.contentPlaceholder')}
              value={formData.content}
              onChange={e => setFormData(f => ({ ...f, content: e.target.value }))}
            />
          </div>
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>
              {t('actions.cancel')}
            </button>
            <button type="submit" className="btn-primary">
              {isEditing ? t('actions.update') : t('actions.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="vocabulary-management-scroll-wrapper">
      <div className="vocabulary-management-page">
        {renderSchoolTabs()}
        {renderYearTabs()}
        {renderContent()}
        {renderForm()}
      </div>
    </div>
  );
};

export default VocabularyManagement; 